import React from 'react';
import { Clock, Calendar, Route, Fuel, DollarSign, Users, Navigation2 } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета времени в пути
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование времени в пути
  const formatTime = (timeInHours) => {
    const hours = Math.floor(timeInHours);
    const minutes = Math.round((timeInHours - hours) * 60);
    
    if (hours === 0) {
      return `${minutes} мин`;
    } else if (minutes === 0) {
      return `${hours} ч`;
    } else {
      return `${hours} ч ${minutes} мин`;
    }
  };

  // Форматирование даты и времени
  const formatDateTime = (date) => {
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ToolResult title="Результаты расчета маршрута">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Время в пути */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Время в пути
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatTime(results.totalTime)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Учтены остановки, перерывы и дорожные условия
          </p>
        </div>

        {/* Прибытие */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-4 h-4"/>
            Время прибытия
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {results.arrivalTime.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}
            </span>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              {results.arrivalTime.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long'})}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Прогнозируемое время прибытия в пункт назначения
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Расстояние" 
          value={`${formatNumber(results.distance, 1)} км`} 
          color="blue" 
          icon={<Route className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Средняя скорость" 
          value={`${formatNumber(results.effectiveSpeed, 1)} км/ч`} 
          color="blue" 
          icon={<Navigation2 className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Чистое время в пути" 
          value={formatTime(results.baseTime)} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.breaksTime > 0 && (
          <ResultItem 
            label="Время на перерывы" 
            value={formatTime(results.breaksTime)} 
            color="orange"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        {results.stopsTime > 0 && (
          <ResultItem 
            label="Время на остановки" 
            value={formatTime(results.stopsTime)} 
            color="orange"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Время отправления" 
          value={formatDateTime(results.departureTime)} 
        />
        
        <ResultItem 
          label="Время прибытия" 
          value={formatDateTime(results.arrivalTime)} 
          color="green"
        />
        
        <ResultItem 
          label="Расход топлива" 
          value={`${formatNumber(results.fuelNeeded, 1)} л`} 
          color="blue" 
          icon={<Fuel className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Стоимость топлива" 
          value={formatCurrency(results.fuelCost, 0)} 
          color="blue" 
        />
        
        {results.tollCost > 0 && (
          <ResultItem 
            label="Стоимость платных дорог" 
            value={formatCurrency(results.tollCost, 0)} 
            color="blue" 
            icon={<DollarSign className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Общая стоимость поездки" 
          value={formatCurrency(results.totalCost, 0)} 
          color="blue"
        />

        {results.numPassengers > 1 && (
          <ResultItem 
            label="Стоимость на человека" 
            value={formatCurrency(results.costPerPerson, 0)} 
            color="green" 
            icon={<Users className="w-4 h-4"/>} 
          />
        )}

        {/* Информация о дорожных условиях */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Route className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Учтенные дорожные условия</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Тип дороги: <span className="font-medium text-blue-600 dark:text-blue-400">{results.roadTypeName}</span>, 
                Траффик: <span className="font-medium text-blue-600 dark:text-blue-400">{results.trafficLevelName}</span>, 
                Погода: <span className="font-medium text-blue-600 dark:text-blue-400">{results.weatherConditionName}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;